import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type AppSidebarFlyoutPanelProps = {
    anchorEl: HTMLElement | null;
    visible: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    children: React.ReactNode;
};

const VIEWPORT_PADDING = 12;

const AppSidebarFlyoutPanel = ({
    anchorEl,
    visible,
    onMouseEnter,
    onMouseLeave,
    children,
}: AppSidebarFlyoutPanelProps) => {
    const flyoutRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (!visible || !anchorEl) {
            return;
        }

        const updatePosition = () => {
            const rect = anchorEl.getBoundingClientRect();
            const flyoutEl = flyoutRef.current;
            const flyoutHeight = flyoutEl?.offsetHeight ?? 0;
            const flyoutWidth = flyoutEl?.offsetWidth ?? 0;

            let top = rect.top;
            let left = rect.right + 6;

            if (flyoutHeight > 0) {
                const maxTop = window.innerHeight - flyoutHeight - VIEWPORT_PADDING;
                top = Math.max(VIEWPORT_PADDING, Math.min(top, maxTop));
            }

            if (flyoutWidth > 0 && left + flyoutWidth > window.innerWidth - VIEWPORT_PADDING) {
                left = Math.max(VIEWPORT_PADDING, rect.left - flyoutWidth - 6);
            }

            setPosition({ top, left });
        };

        updatePosition();
        requestAnimationFrame(updatePosition);

        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        const flyoutEl = flyoutRef.current;
        const resizeObserver =
            flyoutEl && typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(updatePosition)
                : null;

        if (flyoutEl && resizeObserver) {
            resizeObserver.observe(flyoutEl);
        }

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
            resizeObserver?.disconnect();
        };
    }, [anchorEl, visible, children]);

    if (!visible || !anchorEl) {
        return null;
    }

    return createPortal(
        <div
            ref={flyoutRef}
            className="app-sidebar-flyout app-sidebar-flyout--portal"
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex: 1100,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </div>,
        document.body
    );
};

export default AppSidebarFlyoutPanel;
